import styled from 'styled-components';

import { ANIM, CASINO_SETTINGS, COLORS, COUNTRY_MAP_INK, GRADIENTS, MOBILE, SPACE } from '../../../../theme/tokens';
import { Divider, Text } from '../../../../components';
import { WorldMap } from '../../../customizer/country/WorldMap';

/**
 * List on the left, map on the right at the list column's full height (the grid row stretches it), and the summary
 * row under both. On mobile the map leads, then the summary, then the list, so the effect of a switch shows above it.
 */
export const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 2fr) 3fr;
  gap: ${SPACE[20]};

  ${MOBILE} {
    grid-template-columns: 1fr;
    gap: ${SPACE[12]};
  }
`;

export const ListCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  min-width: 0;

  ${MOBILE} {
    order: 3;
  }
`;

export const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  height: 44px;
  padding: 0 ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-within {
    border-color: ${COLORS.accent};
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${COLORS.textStrong};
    font-size: 14px;
  }

  input::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/** The blocked count (a filter toggle) on the left, Allow all / Block all on the right. Wraps on a narrow column. */
export const ListHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
  min-height: 28px;
`;

/**
 * The blocked count, which toggles the list to only the blocked countries. Idle it reads as the plain muted count
 * (the negative margin lines its text up with the list edge); on, it becomes a pill with a close mark.
 */
export const BlockedFilter = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px; /* off-scale: optical gap between the 8px dot and its label, as in the legend */
  margin-left: calc(-1 * ${SPACE[8]});
  padding: ${SPACE[2]} ${SPACE[8]};
  border: 1px solid ${(p) => (p.$active ? COLORS.panelBorder : 'transparent')};
  border-radius: 99px;
  background: ${(p) => (p.$active ? COLORS.surfaceCard : 'transparent')};
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 400;
  color: ${(p) => (p.$active ? COLORS.textStrong : COLORS.textMuted)};
  transition:
    background ${ANIM.INTERACT_MS}ms,
    color ${ANIM.INTERACT_MS}ms,
    border-color ${ANIM.INTERACT_MS}ms;

  &:hover:not(:disabled) {
    color: ${COLORS.textStrong};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

export const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
`;

/** The shared vertical hairline between Allow all and Block all, cut to about the height of their capitals. */
export const QuickActionsDivider = styled(Divider)`
  align-self: center;
  height: 12px;
  background: ${COLORS.inlineDivider};
`;

export const QuickAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.ctaSolid};

  &:disabled {
    cursor: default;
    color: ${COLORS.textFaint};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/** The scrolling country list; about nine rows tall, so the card keeps a steady height. */
export const List = styled.ul`
  margin: 0;
  /* 4px inset keeps the focus ring of the first and last rows' switches inside the scroll box. */
  padding: ${SPACE[4]};
  list-style: none;
  height: 360px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceFaint};

  ${MOBILE} {
    height: 300px;
  }
`;

/**
 * One country: its name, then its switch. A click anywhere on the row flips it (the switch alone takes the focus).
 * Rows are split by the standard fading hairline, inset to the row padding; the two around a hovered row step aside so
 * the hover wash reads as one clean shape.
 */
export const Row = styled.li<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]};
  border-radius: 8px;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};

  &:hover {
    background: ${COLORS.hoverWash};
  }

  /* The shared switch keeps its 48px track; a long name ellipsizes instead of squeezing it. */
  & > [role='switch'] {
    flex: none;
  }

  & + &::before {
    content: '';
    position: absolute;
    top: 0;
    left: ${SPACE[8]};
    right: ${SPACE[8]};
    height: 1px;
    background: ${GRADIENTS.dividerStyledH};
    pointer-events: none;
    transition: opacity ${ANIM.INTERACT_MS}ms;
  }

  &:hover::before,
  &:hover + &::before {
    opacity: 0;
  }
`;

export const RowName = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.text};
`;

/** Small status dot (blocked count, legend) in the map's green / red ink: its lighter stop, so 8px still reads. */
export const ToneDot = styled.span<{ $blocked: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: ${(p) => (p.$blocked ? COUNTRY_MAP_INK.blocked.to : COUNTRY_MAP_INK.allowed.to)};
`;

export const EmptyRow = styled(Text)`
  display: block;
  padding: ${SPACE[16]} ${SPACE[8]};
  font-size: 13px;
  color: ${COLORS.textMuted};
  text-align: center;
`;

/** `url(#…)` paints for the map's countries: per tone, the two diagonals that alternate from country to country. */
export type MapInkPaints = Record<'allowed' | 'blocked', readonly [even: string, odd: string]>;

/**
 * The customizer's world map, re-skinned for a Settings panel: the darker tile fill as its ground instead of the
 * wizard's dark glass, and every allowed / blocked country filled with its own COUNTRY_MAP_INK diagonal (`$paints`),
 * the direction flipping from one country to the next (`nth-child`, in the map's path order). On desktop it fills its
 * grid cell, as tall as the list column next to it (the drawing keeps its proportion, centred); on mobile it takes the
 * source map's 2:1 proportion. `&&` lifts these rules above the map's own (equal-specificity) defaults.
 */
export const CountryMap = styled(WorldMap)<{ $paints: MapInkPaints }>`
  && {
    min-width: 0;
    border: 1px solid ${CASINO_SETTINGS.mapFrameBorder};
    background: ${CASINO_SETTINGS.mapGround};
  }

  ${MOBILE} {
    && {
      order: 1;
      height: auto;
      aspect-ratio: 2 / 1;
    }
  }

  && path {
    fill: ${CASINO_SETTINGS.mapNoTone};
    stroke: ${CASINO_SETTINGS.mapGround};
  }

  && path.allowed {
    fill: ${(p) => p.$paints.allowed[0]};
  }

  && path.allowed:nth-child(odd) {
    fill: ${(p) => p.$paints.allowed[1]};
  }

  && path.blocked {
    fill: ${(p) => p.$paints.blocked[0]};
  }

  && path.blocked:nth-child(odd) {
    fill: ${(p) => p.$paints.blocked[1]};
  }
`;

/** Summary + legend, spanning both columns under the list and map; right under the map on mobile. */
export const MapFoot = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[8]} ${SPACE[16]};

  ${MOBILE} {
    order: 2;
  }
`;

export const Summary = styled(Text)`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${COLORS.text};
`;

export const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[16]};
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px; /* off-scale: optical gap between the 8px dot and its label */
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const ErrorText = styled(Text)`
  display: block;
  font-size: 13px;
  line-height: 18px;
  color: ${COLORS.danger};
`;

export const MutedNote = styled(Text)`
  display: block;
  font-size: 13px;
  font-weight: 300;
  line-height: 18px;
  color: ${COLORS.textMuted};
`;

export const FailedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[12]};
`;
